import React, { useState, useEffect, useCallback } from 'react';
import { Icon, CopyButton } from '../components/ui';
import QueryBar from '../components/QueryBar';
import { useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';

interface ViolationsProps {
  apiUrl: string;
}

interface Violation {
  id: string;
  timestamp: string;
  agent_id: string;
  device_hostname?: string | null;
  display_name?: string | null;
  violation_type: string;
  pattern_name?: string;
  severity: string;
  action_taken: string;
  service?: string;
  tool_name?: string;
  status?: string;
  response_status?: string;
  org_id?: string;
  result?: string;
  arguments?: string;
}

interface Stats {
  total_requests: number;
  total_violations: number;
  total_anomalies: number;
  active_agents: number;
  errors?: number;
}

const getSEVERITIES = (t: (key: string) => string) => [
  { key: 'high', label: t('pages.violations.high'), color: 'var(--danger)' },
  { key: 'medium', label: t('pages.violations.medium'), color: 'var(--warning)' },
  { key: 'low', label: t('pages.violations.low'), color: 'var(--info)' },
] as const;

const PAGE_SIZE = 25;

// Default view: last 24h, so the screen lands with data instead of a blank table.
const DEFAULT_QUERY = 'earliest=24h latest=now';

const Violations: React.FC<ViolationsProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const [violations, setViolations] = useState<Violation[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState(DEFAULT_QUERY);
  const [severityFilter, setSeverityFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ high: 0, medium: 0, low: 0 });
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchViolations = useCallback(async (q: string, sev: string, off: number) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ q, limit: String(PAGE_SIZE), offset: String(off) });
      if (sev) params.append('severity', sev);
      const res = await fetch(`${apiUrl}/admin/violations/search?${params}`);
      if (res.ok) {
        const data = await res.json();
        setViolations(data.violations || []);
        setTotal(data.total || 0);
        setCounts(data.counts || { high: 0, medium: 0, low: 0 });
      }
    } catch (e) {
      console.error('Failed to fetch violations:', e);
    } finally {
      setLoading(false);
    }
    setLastUpdated(new Date());
  }, [apiUrl]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/admin/stats`);
      if (res.ok) setStats(await res.json());
    } catch (e) {
      console.error('Failed to fetch stats:', e);
    }
  }, [apiUrl]);

  const handleSearch = (q: string) => {
    setQuery(q);
    setOffset(0);
    fetchViolations(q, severityFilter, 0);
  };

  const handleSeverityToggle = (sev: string) => {
    const newSev = severityFilter === sev ? '' : sev;
    setSeverityFilter(newSev);
    setOffset(0);
    fetchViolations(query, newSev, 0);
  };

  useEffect(() => {
    fetchViolations(DEFAULT_QUERY, '', 0);
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const start = offset;

  const severityClass = (s: string) =>
    s === 'high' ? 'badge-error' : s === 'medium' ? 'badge-redact' : 'badge-info';

  return (
    <div className="page-content">
      <div className="page-title">
        <div>
          <h1>
            <Icon name="warning" size={20} /> {t('pages.violations.violationsTelemetry')}
          </h1>
          <p className="subtitle">{t('pages.violations.policyBreachesAndAnomaliesDetectedAcross')}</p>
        </div>
      </div>

      <QueryBar apiUrl={apiUrl} onSearch={handleSearch} initialQuery={DEFAULT_QUERY} placeholder={t('pages.violations.searchViolationsEGSeverityHigh')} />

      {/* Consolidated severity breakdown bar */}
      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">
          <h3>{t('pages.violations.severityBreakdown')}</h3>
          <span className="muted-text" style={{ fontSize: 12 }}>{t('pages.violations.totalInView', { v0: stats?.total_violations ?? total, v1: total })}</span>
        </div>
        <div className="ent-card-body">
          <div className="sev-bar" role="img" aria-label={t('pages.violations.severityDistribution')}>
            {total === 0 ? (
              <div className="sev-seg empty" style={{ width: '100%' }} />
            ) : (
              getSEVERITIES(t).map((s) => {
                const c = counts[s.key];
                if (!c) return null;
                const totalSevs = counts.high + counts.medium + counts.low;
                return (
                  <div
                    key={s.key}
                    className="sev-seg"
                    style={{ width: `${(c / totalSevs) * 100}%`, background: s.color }}
                    title={`${s.label}: ${c}`}
                  />
                );
              })
            )}
          </div>
          <div className="sev-legend">
            {getSEVERITIES(t).map((s) => (
              <button
                key={s.key}
                className={`sev-legend-item ${severityFilter === s.key ? 'active' : ''}`}
                onClick={() => handleSeverityToggle(s.key)}
              >
                <span className="sev-dot" style={{ background: s.color }} />
                <span className="sev-name">{s.label}</span>
                <span className="sev-count">{counts[s.key]}</span>
              </button>
            ))}
            {severityFilter && (
              <button className="ent-btn ghost sm" onClick={() => { setSeverityFilter(''); setOffset(0); fetchViolations(query, '', 0); }}>
                <Icon name="close" />
                {t('pages.violations.clearFilter')}
              </button>
            )}
            {lastUpdated && (
              <span className="muted-text" style={{ marginLeft: 'auto', fontSize: 12 }}>{t('pages.violations.updated', { v0: lastUpdated.toLocaleTimeString() })}</span>
            )}
          </div>
        </div>
      </div>

      {/* Violations Table */}
      {loading && violations.length === 0 ? (
        <p className="muted-text" style={{ textAlign: 'center', padding: 32 }}>
          {t('pages.violations.loadingViolations')}
        </p>
      ) : violations.length > 0 ? (
        <>
          <div className="ent-table-wrap scroll">
            <table className="ent-table">
              <thead>
                <tr>
                  <th style={{ width: 168 }}>{t('pages.violations.timestamp')}</th>
                  <th>{t('pages.violations.agentDevice')}</th>
                  <th>{t('pages.violations.type')}</th>
                  <th>{t('pages.violations.pattern')}</th>
                  <th style={{ width: 110 }}>{t('pages.violations.severity')}</th>
                  <th>{t('pages.violations.action')}</th>
                </tr>
              </thead>
              <tbody>
                {violations.map((v) => {
                  const isExpanded = expandedId === v.id;
                  return [
                    <tr
                      key={`row-${v.id}`}
                      className={`violation-${v.severity} ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => setExpandedId(isExpanded ? null : v.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="mono" style={{ color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
                        {parseServerDate(v.timestamp).toLocaleString()}
                      </td>
                      <td className="mono">
                        <span className="copy-cell">
                          <span title={v.agent_id}>{v.device_hostname || v.display_name || v.agent_id}</span>
                          <CopyButton value={v.agent_id} />
                        </span>
                      </td>
                      <td>{v.violation_type}</td>
                      <td style={{ color: 'var(--text-2)' }}>{v.pattern_name || '—'}</td>
                      <td>
                        <span className={`badge ${severityClass(v.severity)}`}>{v.severity}</span>
                      </td>
                      <td style={{ color: 'var(--text-2)' }}>{v.action_taken}</td>
                    </tr>,
                    isExpanded && (
                      <tr key={`detail-${v.id}`} className="audit-detail-row">
                        <td colSpan={6}>
                          <div className="audit-detail">
                            <div className="audit-detail-grid">
                              <div><strong>{t('pages.violations.id')}</strong> {v.id}</div>
                              <div><strong>{t('pages.violations.timestamp2')}</strong> {parseServerDate(v.timestamp).toLocaleString()}</div>
                              <div><strong>{t('pages.violations.agent')}</strong> {v.agent_id}</div>
                              {(v.device_hostname || v.display_name) && (
                                <div><strong>{t('pages.violations.device')}</strong> {v.device_hostname || v.display_name}</div>
                              )}
                              <div><strong>{t('pages.violations.service')}</strong> {v.service || '—'}</div>
                              <div><strong>{t('pages.violations.tool')}</strong> {v.tool_name || '—'}</div>
                              <div><strong>{t('pages.violations.status')}</strong> {v.status || '—'}</div>
                              <div><strong>{t('pages.violations.httpStatus')}</strong> {v.response_status || '—'}</div>
                              <div><strong>{t('pages.violations.organization')}</strong> {v.org_id || t('pages.violations.default')}</div>
                            </div>
                            {v.result && (
                              <div className="audit-detail-result">
                                <strong>{t('pages.violations.fullResult')}</strong>
                                <pre>{v.result}</pre>
                              </div>
                            )}
                            {v.arguments && (
                              <div className="audit-detail-result">
                                <strong>{t('pages.violations.arguments')}</strong>
                                <pre>{v.arguments}</pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ),
                  ];
                })}
              </tbody>
            </table>
          </div>
          <div className="pagination">
            <span className="pagination-info">{t('pages.violations.showingOf', { v0: start + 1, v1: Math.min(start + PAGE_SIZE, total), v2: total })}</span>
            <div className="pagination-controls">
              <button className="ent-btn" onClick={() => { setOffset(0); fetchViolations(query, severityFilter, 0); }} disabled={currentPage === 1}>«</button>
              <button className="ent-btn" onClick={() => { const newOff = Math.max(0, offset - PAGE_SIZE); setOffset(newOff); fetchViolations(query, severityFilter, newOff); }} disabled={currentPage === 1}>{t('pages.violations.prev')}</button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const half = 2;
                let startPage = Math.max(1, currentPage - half);
                const endPage = Math.min(totalPages, startPage + 4);
                startPage = Math.max(1, endPage - 4);
                return startPage + i;
              }).filter((p) => p >= 1 && p <= totalPages).map((p) => (
                <button key={p} className={`ent-btn${p === currentPage ? ' primary' : ''}`} onClick={() => { const newOff = (p - 1) * PAGE_SIZE; setOffset(newOff); fetchViolations(query, severityFilter, newOff); }}>
                  {p}
                </button>
              ))}
              <button className="ent-btn" onClick={() => { const newOff = Math.min((totalPages - 1) * PAGE_SIZE, offset + PAGE_SIZE); setOffset(newOff); fetchViolations(query, severityFilter, newOff); }} disabled={currentPage === totalPages}>{t('pages.violations.next')}</button>
              <button className="ent-btn" onClick={() => { const newOff = (totalPages - 1) * PAGE_SIZE; setOffset(newOff); fetchViolations(query, severityFilter, newOff); }} disabled={currentPage === totalPages}>»</button>
            </div>
          </div>
        </>
      ) : (
        <div className="ent-empty">
          <span className="icon">
            <Icon name="checkCircle" />
          </span>
          <h4>{severityFilter ? t('pages.violations.noViolationsAtSeverity', { severity: severityFilter }) : t('pages.violations.noViolations')}</h4>
          <p>{t('pages.violations.thePerimeterIsSecureNoPolicy')}</p>
        </div>
      )}
    </div>
  );
};

export default Violations;
